import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function HowItwork() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1.2, 1]);
  const backgroundOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 0.5, 0.3]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="relative pb-20 bg-[#000] overflow-hidden" id="how-it-works" ref={sectionRef}>
      <motion.div 
        className="absolute inset-0 howItwork-section"
        style={{ 
          scale: backgroundScale,
          opacity: backgroundOpacity 
        }}
      />
      <div className="relative pt-20 lg:pt-40 text-center z-10">
        <motion.div
          className="container mx-auto z-[100] relative"
          initial={{ opacity: 0, y: -30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          ref={ref}
        >
          <motion.h1
            id="gettingstarted"
            className="text-[28px] md:text-[42px] lg:text-[56px] text-[#fff] font-bold mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            How <span className="text-[#8ABB01]">Nudgie</span> AI works
          </motion.h1>
          <motion.p
            className="text-[18px] lg:text-[24px] text-[#fff] mb-8 md:mb-20"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Get started in just minutes and transform your life
          </motion.p>
        </motion.div>
      </div>
      <div className="container mx-auto px-4">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data.map((item, index) => {
            return (
              <motion.div
                key={item.id}
                className="relative text-center bg-[#F2FCDA] rounded-xl px-8 md:px-16 pt-8 md:pt-16 border-4 border-[#8ABB01] h-full hover:shadow-xl"
                variants={itemVariants}
              >
              <div
                className="h-[60px] w-[60px] mx-auto rounded-full bg-[#AFD24F] flex items-center justify-center mb-4"
              >
                <h6 className="text-[24px] font-bold">{item.id}</h6>
              </div>
              <h4 className="text-[18px] md:text-[24px] font-bold mb-4">
                {item.name}
              </h4>
              <p className="text-[16px] md:text-[16px] mb-2 text-center md:max-w-[370px] md:mx-auto">
                {item.detail}
              </p>
              <img
                src={item.img}
                alt={item.name}
                className="h-[200px] md:h-[300px] w-full"
                style={{
                  objectFit: "contain",
                }}
              />
            </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

const data = [
  {
    id: 1,
    img: "signup.svg",
    name: "Sign Up & Set Goals",
    detail: "Create an account and tell us what you want to achieve.",
  },
  {
    id: 2,
    img: "plan.svg",
    name: "Receive Your Personalized Plan",
    detail:
      "Receive a thorough plan that takes your schedule, learning style, and experience into account.",
  },
  {
    id: 3,
    img: "4.1.png",
    name: "Get Held Accountable",
    detail:
      "Nudgie will text you whenever you have a task coming up. He'll expect an answer.",
  },
  {
    id: 4,
    img: "metrics.svg",
    name: "Track Your Progress",
    detail:
      "Our intelligent metrics-tracking systems make it easy for you to see how you're doing.",
  },
];
